import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { useTranslation } from "react-i18next"
import { cloneDeep, isEqual } from "lodash"

import {
  useReportTemplateDetailQuery,
  useReportTemplateMetasQuery,
  useUpsertReportTemplateMutation,
} from "@/hooks/api/useReportQuery"
import { ReportTemplate, Section } from "@/types/report"
import { GraphSettings, StatsSettings } from "@/app/report/common/type"
import BasicButton from "@/components/button/BasicButton"
import { SECTION_TYPE } from "@/app/report/common/constants"
import { isEqualUnordered } from "@/app/report/common/tools"
import { GROUP_KEY_MAPPING } from "@/app/report/hooks/useCustomTableData"
import { observeMuiErrors } from "@/utils/error"
import { createGlobalStore } from "@/stores/global"
import useRouter, { RouterEvent } from "@/hooks/router/useRoute"
import { useBlockBrowserBack } from "@/hooks/router/useBlockBrowserBack"
import { colors } from "@/components/colors"

import SettingSectionContainer from "@/app/report/components/setting/SettingSectionContainer"
import GraphSection from "@/app/report/trend/settings/components/section/GraphSection"
import CustomTableSettingsSection from "@/app/report/trend/settings/components/section/CustomTableSettingsSection"
import SectionDivider from "@/app/report/components/setting/SectionDivider"
import DeleteItemDialog from "@/app/report/components/setting/DeleteItemDialog"
import "./trendReportSettings.scss"
import SettingTemplateSelector from "@/app/report/components/setting/SettingTemplateContainer.tsx"

export default function TrendReportSettings() {
  const { t } = useTranslation()
  const router = useRouter()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const [idfTemplate, setIdfTemplate] = useState<number | null>(null)
  const [tempTemplateState, setTempTemplateState] = useState<ReportTemplate | null>(null)
  const [settingContentList, setSettingContentList] = useState<Section[]>([])
  const [draggingSectionKey, setDraggingSectionKey] = useState<string | null>(null)
  const [hasSectionError, setHasSectionError] = useState(false)
  const [isClickedSave, setIsClickedSave] = useState(false)
  const [hoveredPosition, setHoveredPosition] = useState<number | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [deleteSectionIndex, setDeleteSectionIndex] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const { data: templates, refetch: templateMetaRefetch } = useReportTemplateMetasQuery({
    reportType: "trend",
  })

  const { data: template, refetch: templateRefetch } = useReportTemplateDetailQuery(
    idfTemplate as number,
  )
  const { mutateAsync: reportTemplateMutate, isPending } = useUpsertReportTemplateMutation()

  useEffect(() => {
    if (idfTemplate !== null) return
    const lastUsed = templates?.find((template) => template.isLastUsed)

    if (lastUsed) {
      setIdfTemplate(lastUsed.idfTemplate)
    }
  }, [templates])

  useEffect(() => {
    if (template) {
      setTempTemplateState(cloneDeep(template) || null)
      setSettingContentList(cloneDeep(template.content) || [])
    }
  }, [template])

  useEffect(() => {
    let prevErr = false

    const observer = observeMuiErrors((hasErr) => {
      if (prevErr !== hasErr) {
        setHasSectionError(hasErr)
        prevErr = hasErr
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  const newSettingContentList = useMemo(
    () => [
      ...settingContentList,
      { section: "__dummy__placeholder__", active: false, settings: [] },
    ],
    [settingContentList],
  )

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingSectionKey(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingSectionKey(null)

    const dragOver = document.querySelector(".SettingSectionDragOver")
    const dragOverOrigin = document.querySelector(".SettingSectionDragOverOrigin")

    if (
      dragOver?.nextElementSibling === dragOverOrigin ||
      dragOver?.previousElementSibling === dragOverOrigin
    ) {
      return
    }

    const { active, over } = event
    const oldIndex = newSettingContentList.findIndex(
      (cont, idx) => `${cont.section}-${idx}` === active?.id,
    )
    const newIndex = newSettingContentList.findIndex(
      (cont, idx) => `${cont.section}-${idx}` === over?.id,
    )

    const targetIndex = newIndex > oldIndex ? newIndex - 1 : newIndex === -1 ? oldIndex : newIndex

    setSettingContentList(arrayMove(settingContentList, oldIndex, targetIndex))
    setTempTemplateState((prev) => {
      if (!prev) {
        return null
      }

      return { ...prev, content: arrayMove(prev.content, oldIndex, targetIndex) }
    })
  }

  const draggingContent = useMemo(() => {
    if (!draggingSectionKey) return null
    const index = settingContentList.findIndex(
      (cont, idx) => `${cont.section}-${idx}` === draggingSectionKey,
    )
    return index >= 0 ? settingContentList[index] : null
  }, [draggingSectionKey, settingContentList])

  const availableSections = useMemo(
    // Stats by Activity / by Player 는 폐지 예정 — 신규 추가 불가, 기존 템플릿은 로드 시 Custom Table 로 대치된다 (#99)
    () => [SECTION_TYPE.ACTIVITY_GRAPH, SECTION_TYPE.PLAYER_GRAPH, SECTION_TYPE.CUSTOM_TABLE],
    [],
  )

  const sectionCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    settingContentList.forEach((section) => {
      counts[section.section] = (counts[section.section] || 0) + 1
    })
    return counts
  }, [settingContentList])

  const getDefaultSettings = (sectionType: string) => {
    const currentCount = sectionCounts[sectionType] || 0

    switch (sectionType) {
      case SECTION_TYPE.ACTIVITY_GRAPH:
        return [
          {
            dataLabel: "hideValues",
            decimalPlace: 1,
            title: `Chart by Activity ${currentCount + 1}`,
            calcType: "average",
            yAxis: [
              {
                metric: "",
                chartType: "",
                color: colors.blue[900],
                manualScaleActive: false,
                manualScaleValue: null,
              },
            ],
            sortSetting: { key: "date", order: "asc" },
          },
        ]
      case SECTION_TYPE.PLAYER_GRAPH:
        return [
          {
            dataLabel: "hideValues",
            decimalPlace: 1,
            title: `Chart by Player ${currentCount + 1}`,
            calcType: "average",
            yAxis: [
              {
                metric: "",
                chartType: "",
                color: colors.blue[900],
                manualScaleActive: false,
                manualScaleValue: null,
              },
            ],
            sortSetting: { key: "jersey", order: "asc" },
          },
        ]
      case SECTION_TYPE.CUSTOM_TABLE: {
        const groupKey1 = "activity"
        const { columnKey, info } = GROUP_KEY_MAPPING[groupKey1]

        return {
          title: `Table ${currentCount + 1}`,
          calcType: "average",
          // 기본 그룹에서 유도된 컬럼은 기본 좌측 고정 (이후 해제 상태는 저장값을 존중 — WEB-119)
          fixedColumns: [columnKey, info].filter(Boolean),
          metrics: [],
          groupKey1,
          // 새 Table 은 요약 행(Total·Average) 둘 다 표시가 기본 (#155)
          summaryRows: { total: true, average: true },
        }
      }
      default:
        return []
    }
  }

  const handleMenuStateChange = useCallback((isOpen: boolean) => {
    setIsMenuOpen(isOpen)
  }, [])

  const handleAddSection = (sectionType: string, position: number) => {
    const newSection: Section = {
      section: sectionType,
      active: true,
      settings: getDefaultSettings(sectionType),
    }

    setSettingContentList((prev) => {
      const updated = [...prev]
      updated.splice(position, 0, newSection)

      return updated
    })

    setTempTemplateState((prev) => {
      if (!prev) {
        return null
      }

      const updatedContent = [...prev.content]
      updatedContent.splice(position, 0, newSection)

      return { ...prev, content: updatedContent }
    })

    setHoveredPosition(null)
    setIsMenuOpen(false)
  }

  const handleDeleteSection = (index: number) => {
    setSettingContentList((prev) => prev.filter((_, i) => i !== index))
    setTempTemplateState((prev) => {
      if (!prev) {
        return null
      }

      return { ...prev, content: prev.content.filter((_, i) => i !== index) }
    })
  }

  useEffect(() => {
    if (!containerRef.current || draggingSectionKey || availableSections.length === 0) {
      return
    }

    const container = containerRef.current

    const handleMouseMove = (e: MouseEvent) => {
      if (!container) {
        return
      }

      const sections = container.querySelectorAll(".SettingSectionCont")
      const mouseY = e.clientY

      let closestPosition = 0
      let minDistance = Infinity

      const templateSelector = container.querySelector(".SettingTemplateSelectorContainer")
      if (templateSelector) {
        const rect = templateSelector.getBoundingClientRect()
        if (mouseY < rect.bottom + 20) {
          setHoveredPosition(null)

          return
        }
      }

      const saveButton = container.querySelector(".trend-settings-save-button")

      if (saveButton) {
        const rect = saveButton.getBoundingClientRect()
        if (mouseY > rect.top - 20) {
          setHoveredPosition(null)
          return
        }
      }

      let isOverSection = false
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect()
        const sectionTop = rect.top
        const sectionBottom = rect.bottom

        if (mouseY >= sectionTop && mouseY <= sectionBottom) {
          isOverSection = true

          return
        }

        const distanceToTop = Math.abs(mouseY - sectionTop)
        if (distanceToTop < minDistance) {
          minDistance = distanceToTop
          closestPosition = index
        }

        const distanceToBottom = Math.abs(mouseY - sectionBottom)
        if (distanceToBottom < minDistance) {
          minDistance = distanceToBottom
          closestPosition = index + 1
        }
      })

      if (isOverSection) {
        setHoveredPosition(null)

        return
      }

      if (sections.length === 0) {
        closestPosition = 0
      }

      if (sections.length > 1) {
        const lastRealSection = sections[sections.length - 2]
        if (lastRealSection) {
          const rect = lastRealSection.getBoundingClientRect()

          const distanceFromBottom = mouseY - rect.bottom
          if (distanceFromBottom > 0 && distanceFromBottom <= 60) {
            closestPosition = sections.length - 1
          }
        }
      }

      setHoveredPosition(closestPosition)
    }

    const handleMouseLeave = () => {
      if (!isMenuOpen) {
        setHoveredPosition(null)
      }
    }

    container.addEventListener("mousemove", handleMouseMove)
    container.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      container.removeEventListener("mousemove", handleMouseMove)
      container.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [draggingSectionKey, availableSections.length, settingContentList.length, isMenuOpen])

  const renderSettingContent = useCallback(
    (content: Section, index: number) => {
      switch (content.section) {
        case SECTION_TYPE.ACTIVITY_GRAPH:
          return (
            <GraphSection
              key={`${content.section}-${index}`}
              settings={content.settings as GraphSettings[]}
              tempTemplate={tempTemplateState}
              setTempTemplate={setTempTemplateState}
              sectionIndex={index}
            />
          )
        case SECTION_TYPE.PLAYER_GRAPH:
          return (
            <GraphSection
              key={`${content.section}-${index}`}
              settings={content.settings as GraphSettings[]}
              tempTemplate={tempTemplateState}
              setTempTemplate={setTempTemplateState}
              sectionType="player"
              sectionIndex={index}
            />
          )
        case SECTION_TYPE.CUSTOM_TABLE:
          return (
            <CustomTableSettingsSection
              key={`${content.section}-${index}`}
              settings={content.settings as StatsSettings}
              setTempTemplate={setTempTemplateState}
              sectionIndex={index}
            />
          )
        default:
          return null
      }
    },
    [setTempTemplateState, tempTemplateState],
  )

  const isEqualContent = useMemo(() => {
    if (!tempTemplateState || !template) return true

    const isEqualTemplateName = template.name === tempTemplateState.name
    const isEqualContent = isEqual(tempTemplateState.content, template.content)

    return isEqualTemplateName && isEqualContent
  }, [tempTemplateState, template])

  const isDisabledSaveButton = useMemo(() => {
    return isEqualContent || hasSectionError || isPending
  }, [isEqualContent, hasSectionError, isPending])

  useBlockBrowserBack(!isEqualContent)

  useEffect(() => {
    if (isClickedSave) {
      return
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isEqualContent) {
        e.preventDefault()
      }
    }

    const handleChangeState = () => {
      if (!isEqualContent) {
        const confirmed = confirm(t("messages.confirmLeavePage"))

        return !confirmed
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    router?.addEventListener(RouterEvent.routeChangeStart, handleChangeState)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      router?.removeEventListener(RouterEvent.routeChangeStart, handleChangeState)
    }
  }, [isEqualContent, router, isClickedSave])

  const onSave = async () => {
    if (!tempTemplateState) {
      return
    }

    try {
      await reportTemplateMutate(tempTemplateState)
      await Promise.all([templateRefetch(), templateMetaRefetch()])
      setIsClickedSave(true)
    } catch (error) {
      console.error(error)
      await templateMetaRefetch()
      setIsClickedSave(false)
    }
  }

  useEffect(() => {
    if (isDisabledSaveButton && isClickedSave) {
      setSnackBar({
        open: true,
        message: t("messages.successUpdate"),
        severity: "success",
      })
    }
  }, [isDisabledSaveButton, isClickedSave])

  return (
    <div className="TrendReportSettingsCont" ref={containerRef}>
      <SettingTemplateSelector
        idfCurrentTemplate={idfTemplate}
        template={tempTemplateState}
        setTemplate={setTempTemplateState}
        templates={templates || []}
        onChangeTemplate={setIdfTemplate}
        reportType="trend"
      />
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}>
        {newSettingContentList.map((content, index) => (
          <div key={`${content.section}-${index}`}>
            {hoveredPosition === index && (
              <SectionDivider
                position={index}
                onAddSection={handleAddSection}
                availableSections={availableSections}
                isVisible={true}
                onMenuStateChange={handleMenuStateChange}
                sectionCounts={sectionCounts}
              />
            )}
            {content.section !== "__dummy__placeholder__" && (
              <SettingSectionContainer
                id={`${content.section}-${index}`}
                title={t(content.section)}
                onDeleteSection={() => setDeleteSectionIndex(index)}>
                {renderSettingContent(content, index)}
              </SettingSectionContainer>
            )}
          </div>
        ))}
        <DragOverlay>
          {draggingContent ? (
            <SettingSectionContainer
              id={draggingContent.section}
              title={t(draggingContent.section)}
              isDragging
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <span className="trend-settings-save-button">
        <BasicButton
          type="primary-contained"
          size="lg"
          disabled={isDisabledSaveButton}
          onClick={onSave}>
          {t("button.save", "Save")}
        </BasicButton>
      </span>

      <DeleteItemDialog
        deleteItemIndex={deleteSectionIndex}
        setDeleteItemIndex={setDeleteSectionIndex}
        onDeleteAction={handleDeleteSection}
      />
    </div>
  )
}
